import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { GridList, GridCard, NotFoundCard } from 'src/components'
import {
  useCancelJourneysRemove,
  useItemsRemoveConfirm,
  useNotification,
  useRemoveJourneys,
} from 'src/hooks'
import { TrashTimeoutIcon } from 'src/assets/icons'
import { IJourneyDtoWithId } from 'src/types'
import { IFilter } from 'src/components/filter/types/filter.types'

interface IJourneysGridProps {
  journeys?: IJourneyDtoWithId[] | void
  filter?: IFilter
}

const JourneysGrid = ({ journeys, filter }: IJourneysGridProps) => {
  const [t] = useTranslation()
  const { setRemoveConfirm } = useItemsRemoveConfirm()
  const { mutateAsync } = useRemoveJourneys()
  const { mutateAsync: cancelRemove } = useCancelJourneysRemove()
  const { notify } = useNotification()
  const navigate = useNavigate()

  const handleDelete = (id: string) => async () => {
    const item = journeys?.find((item) => id === item.id)
    if (!item) return

    const stepRelations = !!item.steps.length
      ? [
          {
            title: 'steps',
            relations: item.steps.map((step) => step.name),
          },
        ]
      : []
    const productRelations = !!item.products.length
      ? [
          {
            title: 'products',
            relations: item.products.map((product) => product.name),
          },
        ]
      : []

    setRemoveConfirm({
      handleConfirm: async () => {
        const resp = await mutateAsync([id])
        if (!resp) return
        notify({
          message: t('items-delete-notification.v'),
          icon: <TrashTimeoutIcon />,
          onButtonClick: async () => {
            await cancelRemove({ ids: [id] })
          },
          timeout: 14000,
        })
      },
      itemsRelations: [...stepRelations, ...productRelations],
      itemsTitles: [item.journey_name],
      itemsType: 'journey',
    })
  }

  const handleDuplicate = (id: string) => () => {
    navigate(`add?duplicate=${id}`)
  }

  const createItems = (id: string) => [
    {
      title: t('delete-button-text.v'),
      onClick: handleDelete(id),
    },
    {
      title: t('duplicate-button-text.v'),
      onClick: handleDuplicate(id),
    },
  ]

  return (
    <GridList
      entityCountKey="journeysCount"
      addButtonLT="journey-create-upgrade-text"
      routeToAdd={'add'}
      showUpgrade
    >
      {journeys?.map(({ id, code, image_medium, image, journey_name }) => (
        <GridCard
          key={id}
          code={'#' + code}
          image={image_medium || image}
          name={journey_name}
          route={`${id}`}
          items={createItems(id)}
          filter={filter}
        />
      ))}
      {journeys?.length === 0 && <NotFoundCard text={t('not-found-journeys.v')} />}
    </GridList>
  )
}

export { JourneysGrid }
